import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, BookOpen, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { listAcademics, seedAcademicsDemo } from "@/lib/academics.functions";
import { shortDate } from "@/lib/format";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat"];

export function AcademicsAdmin({ schoolId }: { schoolId: string }) {
  const qc = useQueryClient();
  const load = useServerFn(listAcademics);
  const { data } = useQuery<any>({
    queryKey: ["academics-admin", schoolId], queryFn: () => load({ data: { schoolId } }),
  });
  const seed = useMutation({
    mutationFn: useServerFn(seedAcademicsDemo),
    onSuccess: (r: any) => {
      toast.success(r?.seeded ? "Academics demo seeded" : "Already has academics");
      qc.invalidateQueries({ queryKey: ["academics-admin", schoolId] });
    },
  });

  const terms = data?.terms ?? [];
  const subjects = data?.subjects ?? [];
  const timetable = data?.timetable ?? [];
  const assignments = data?.assignments ?? [];

  const currentTerm = terms.find((t: any) => t.is_current) ?? terms[0];

  return (
    <div className="space-y-4">
      <Card className="relative overflow-hidden bg-gradient-to-br from-rose-800 via-red-800 to-orange-900 p-6 text-rose-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-rose-200/70">Academics</div>
            <div className="mt-1 font-display text-2xl">Terms, timetable, assignments &amp; gradebook</div>
            {currentTerm && <div className="mt-1 text-sm text-rose-100/80">{currentTerm.name} · {shortDate(currentTerm.start_date)} → {shortDate(currentTerm.end_date)}</div>}
          </div>
          <Button variant="secondary" className="bg-rose-50 text-rose-900 hover:bg-rose-100" disabled={seed.isPending} onClick={() => seed.mutate({ data: { schoolId } })}>
            <Sparkles className="mr-1 h-4 w-4" />{seed.isPending ? "Seeding…" : "Seed academics demo"}
          </Button>
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-4">
        <Metric label="Subjects" value={`${subjects.length}`} />
        <Metric label="Timetable slots" value={`${timetable.length}`} />
        <Metric label="Active assignments" value={`${assignments.length}`} />
        <Metric label="Terms" value={`${terms.length}`} />
      </div>

      <Card className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="font-display text-base flex items-center gap-2"><CalendarClock className="h-4 w-4" />Weekly timetable</div>
          <Badge variant="outline" className="text-[10px]">first class shown</Badge>
        </div>
        <div className="overflow-x-auto">
          {(() => {
            const cls = [...new Set(timetable.map((t: any) => t.class_label))][0] as string | undefined;
            const rows = timetable.filter((t: any) => t.class_label === cls);
            const times = [...new Set(rows.map((r: any) => r.start_time))].sort() as string[];
            if (!cls) return <div className="p-6 text-center text-sm text-muted-foreground">No timetable yet — seed the demo to preview.</div>;
            return (
              <table className="min-w-full text-xs">
                <thead><tr><th className="p-2 text-left text-muted-foreground">{cls}</th>{DAYS.map(d => <th key={d} className="p-2 text-left">{d}</th>)}</tr></thead>
                <tbody>
                  {times.map(time => (
                    <tr key={time} className="border-t">
                      <td className="p-2 font-mono text-muted-foreground">{time}</td>
                      {DAYS.map((_, di) => {
                        const s = rows.find((r: any) => r.start_time === time && r.day_of_week === di + 1);
                        return <td key={di} className="p-1">
                          {s && <div className="rounded-md p-1.5" style={{ background: `${s.subjects?.color_hex}22`, borderLeft: `3px solid ${s.subjects?.color_hex}` }}>
                            <div className="font-medium">{s.subjects?.name}</div>
                            <div className="text-[10px] text-muted-foreground">{s.teacher_name} · {s.room}</div>
                          </div>}
                        </td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>
      </Card>

      <Card className="p-4">
        <div className="mb-2 font-display text-base flex items-center gap-2"><BookOpen className="h-4 w-4" />Assignments</div>
        <div className="space-y-1">
          {assignments.slice(0, 12).map((a: any) => (
            <div key={a.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 rounded-md border p-2 text-sm">
              <div>
                <div className="font-medium">{a.title}</div>
                <div className="text-xs text-muted-foreground">{a.subjects?.name} · {a.class_label}</div>
              </div>
              <div className="text-xs text-muted-foreground">due {shortDate(a.due_at)}</div>
              <Badge variant="outline">{a.max_score} pts</Badge>
            </div>
          ))}
          {!assignments.length && <div className="p-4 text-center text-xs text-muted-foreground">No assignments yet</div>}
        </div>
      </Card>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-1 font-display text-xl">{value}</div></Card>;
}
